import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandLogo from "@/components/site/BrandLogo";
import {
  LogOut,
  Package,
  ShoppingBag,
  Plus,
  Edit3,
  Trash2,
  Loader2,
  Image as ImageIcon,
  X,
  Search,
  ExternalLink,
  Gift,
  Share2,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, fileUrl } from "@/lib/api";
import { ORDER_STATUSES, CATALOG_CATEGORIES, CONTACT } from "@/lib/products";
import Seo from "@/components/Seo";
import CatalogImagesTab from "@/pages/admin/CatalogImagesTab";

const TABS = [
  { id: "orders", label: "Orders", icon: <ShoppingBag size={16} /> },
  { id: "bulk", label: "Bulk Enquiries", icon: <Gift size={16} /> },
  { id: "referrals", label: "Referrals", icon: <Share2 size={16} /> },
  { id: "catalog-images", label: "Product Images", icon: <ImageIcon size={16} /> },
  { id: "products", label: "Catalog", icon: <Package size={16} /> },
];

export default function AdminDashboard({ user }) {
  const [tab, setTab] = useState("orders");
  const navigate = useNavigate();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen">
      <Seo title="Admin Dashboard" path="/admin" noindex />
      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-10 h-16">
          <div className="flex items-center gap-3">
            <BrandLogo size={32} />
            <div>
              <div className="font-display gold-gradient-text text-lg leading-none">Shine on Coin Admin</div>
              <div className="text-[10px] uppercase tracking-widest-2 text-white/40 mt-1">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs uppercase tracking-widest-2 text-white/60 hover:text-[#d4af37]" data-testid="admin-view-site">View site</a>
            <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest-2 text-white/60 hover:text-red-400" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-24 pb-16">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition ${
                tab === t.id
                  ? "bg-[#d4af37] text-black"
                  : "glass text-white/80 hover:text-white border border-white/10"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "orders" && <OrdersTab />}
          {tab === "bulk" && <BulkTab />}
          {tab === "referrals" && <ReferralsTab />}
          {tab === "catalog-images" && <CatalogImagesTab />}
          {tab === "products" && <ProductsTab />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Orders ---------------- */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/orders");
      setOrders(res.data || []);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filter !== "all" && o.status !== filter) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          o.customer_name?.toLowerCase().includes(s) ||
          o.customer_phone?.includes(s) ||
          o.product_name?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [orders, filter, q]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: orders.length };
    ORDER_STATUSES.forEach((s) => { c[s.value] = orders.filter((o) => o.status === s.value).length; });
    return c;
  }, [orders]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            All <span className="opacity-60 ml-1">{counts.all}</span>
          </Chip>
          {ORDER_STATUSES.map((s) => (
            <Chip key={s.value} active={filter === s.value} onClick={() => setFilter(s.value)} testid={`filter-${s.value}`}>
              {s.label} <span className="opacity-60 ml-1">{counts[s.value] || 0}</span>
            </Chip>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, product…"
            className="pl-9 bg-white/5 border-white/10 text-white"
            data-testid="orders-search"
          />
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-white/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-white/50">
            No orders {filter !== "all" ? `in “${filter}”` : "yet"}.
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((o) => (
              <OrderRow key={o.id} order={o} onSetStatus={setStatus} onView={() => setViewing(o)} />
            ))}
          </div>
        )}
      </div>

      <OrderDetailsDialog order={viewing} onOpenChange={(open) => !open && setViewing(null)} onSetStatus={setStatus} />
    </div>
  );
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest-2 transition ${
      active ? "bg-[#d4af37] text-black" : "glass text-white/70 hover:text-white border border-white/10"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const OrderRow = ({ order, onSetStatus, onView }) => {
  const statusDef = ORDER_STATUSES.find((s) => s.value === order.status);
  const date = new Date(order.created_at).toLocaleString();
  return (
    <div
      className="glass rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-5"
      data-testid={`order-row-${order.id}`}
    >
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center shrink-0">
        {order.photo_path ? (
          <img src={fileUrl(order.photo_path)} alt="engraving" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="text-white/30" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-display text-lg text-white">{order.customer_name}</div>
          <div className="text-xs text-white/50">· {order.customer_phone}</div>
        </div>
        <div className="mt-1 text-sm text-white/70 truncate">
          <span className="text-[#d4af37]">{order.product_name}</span>
          {order.gift_packaging && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest-2 text-[#d4af37] border border-[#d4af37]/40 rounded-full px-2 py-0.5 align-middle">
              <Gift size={10} /> Gift Wrapped
            </span>
          )}
          {order.customization && Object.entries(order.customization).slice(0, 3).map(([k, v]) => (
            <span key={k} className="ml-2 text-white/50">
              · <span className="uppercase text-[10px] tracking-widest-2">{k}</span> {v}
            </span>
          ))}
        </div>
        <div className="mt-1 text-[11px] text-white/40 uppercase tracking-widest-2">{date}</div>
      </div>
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest-2 border ${statusDef?.color || "text-white"} border-white/15 hover:border-[#d4af37]`}
              data-testid={`order-status-${order.id}`}
            >
              {statusDef?.label || order.status}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="glass-strong border-white/10">
            {ORDER_STATUSES.map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => onSetStatus(order.id, s.value)}
                className="text-white/80 hover:text-white focus:text-white"
                data-testid={`order-set-${order.id}-${s.value}`}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={onView} className="text-xs text-[#d4af37] hover:underline flex items-center gap-1" data-testid={`order-view-${order.id}`}>
          Details <ExternalLink size={12} />
        </button>
      </div>
    </div>
  );
};

const OrderDetailsDialog = ({ order, onOpenChange, onSetStatus }) => {
  if (!order) return null;
  const waMsg = encodeURIComponent(`Hi ${order.customer_name}, this is Shine on Coin. Regarding your order for ${order.product_name}…`);
  return (
    <Dialog open={!!order} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-transparent border-white/10 p-0 overflow-hidden">
        <div className="glass-strong rounded-2xl p-6 md:p-8">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-3xl text-white">{order.product_name}</DialogTitle>
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37] mt-1">Order {order.id.slice(0, 8)}</div>
          </DialogHeader>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div>
              {order.photo_path ? (
                <img src={fileUrl(order.photo_path)} alt="engraving" className="w-full rounded-xl object-cover aspect-square" />
              ) : (
                <div className="rounded-xl aspect-square bg-white/5 flex items-center justify-center text-white/30">No photo</div>
              )}
            </div>
            <div className="space-y-3 text-sm">
              <Row k="Customer" v={order.customer_name} />
              <Row k="Phone" v={order.customer_phone} />
              <Row k="Address" v={order.customer_address} />
              <div className="h-px bg-white/10 my-2" />
              {Object.entries(order.customization || {}).map(([k, v]) => (
                <Row key={k} k={k} v={String(v)} />
              ))}
              {order.notes && <Row k="Notes" v={order.notes} />}
              <div className="h-px bg-white/10 my-2" />
              <Row k="Created" v={new Date(order.created_at).toLocaleString()} />
              <Row k="Updated" v={new Date(order.updated_at).toLocaleString()} />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
            <a
              href={`${CONTACT.whatsapp}?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full px-5 py-2.5 glass border border-white/10 text-white/90 text-sm hover:border-[#d4af37]"
              data-testid="order-whatsapp-customer"
            >
              WhatsApp customer
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-full bg-[#d4af37] text-black hover:brightness-110" data-testid="order-details-status">
                  Set status →
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass-strong border-white/10">
                {ORDER_STATUSES.map((s) => (
                  <DropdownMenuItem key={s.value} onClick={() => onSetStatus(order.id, s.value)} className="text-white/80">
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-3">
    <div className="text-white/40 uppercase tracking-widest-2 text-[10px]">{k}</div>
    <div className="col-span-2 text-white/90 break-words">{v}</div>
  </div>
);

/* ---------------- Bulk Enquiries ---------------- */
function BulkTab() {
  const [bulks, setBulks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bulk-orders");
      setBulks(res.data || []);
    } catch {
      toast.error("Failed to load bulk enquiries");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bulk-orders/${id}/status`, { status });
      setBulks((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const filtered = useMemo(() => bulks.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        b.customer_name?.toLowerCase().includes(s) ||
        b.company?.toLowerCase().includes(s) ||
        b.customer_phone?.includes(s)
      );
    }
    return true;
  }), [bulks, filter, q]);

  const counts = useMemo(() => {
    const c = { all: bulks.length };
    ORDER_STATUSES.forEach((s) => { c[s.value] = bulks.filter((o) => o.status === s.value).length; });
    return c;
  }, [bulks]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="bulk-filter-all">
            All <span className="opacity-60 ml-1">{counts.all}</span>
          </Chip>
          {ORDER_STATUSES.map((s) => (
            <Chip key={s.value} active={filter === s.value} onClick={() => setFilter(s.value)} testid={`bulk-filter-${s.value}`}>
              {s.label} <span className="opacity-60 ml-1">{counts[s.value] || 0}</span>
            </Chip>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search company, name, phone…"
            className="pl-9 bg-white/5 border-white/10 text-white"
            data-testid="bulk-search"
          />
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-white/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading enquiries…</div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-white/50">
            No bulk enquiries yet.
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((b) => (
              <BulkRow key={b.id} bulk={b} onSetStatus={setStatus} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const BulkRow = ({ bulk, onSetStatus }) => {
  const statusDef = ORDER_STATUSES.find((s) => s.value === bulk.status);
  const date = new Date(bulk.created_at).toLocaleString();
  return (
    <div className="glass rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-5" data-testid={`bulk-row-${bulk.id}`}>
      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#f3e5ab]/20 via-[#d4af37]/20 to-transparent flex items-center justify-center text-[#d4af37] shrink-0 border border-[#d4af37]/30">
        <Gift size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-display text-lg text-white">{bulk.company || bulk.customer_name}</div>
          <div className="text-xs text-white/50">· {bulk.customer_name} · {bulk.customer_phone}</div>
        </div>
        <div className="mt-1 text-sm text-white/70 flex flex-wrap gap-x-4">
          <span className="text-[#d4af37]">{bulk.quantity}× {bulk.product_interest}</span>
          <span className="text-white/50">Deliver: <span className="text-white/80">{bulk.delivery_window}</span></span>
          {bulk.occasion && <span className="text-white/50">Occasion: <span className="text-white/80">{bulk.occasion}</span></span>}
        </div>
        {bulk.notes && <div className="mt-1 text-xs text-white/50 line-clamp-1">“{bulk.notes}”</div>}
        <div className="mt-1 text-[11px] text-white/40 uppercase tracking-widest-2">{date}</div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {bulk.customer_email && (
          <a href={`mailto:${bulk.customer_email}`} className="text-xs text-white/60 hover:text-[#d4af37]" data-testid={`bulk-email-${bulk.id}`}>
            Email
          </a>
        )}
        <a
          href={`${CONTACT.whatsapp}?text=${encodeURIComponent(`Hi ${bulk.customer_name}, this is Shine on Coin regarding your bulk enquiry for ${bulk.quantity} pieces…`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-white/60 hover:text-[#d4af37]"
          data-testid={`bulk-wa-${bulk.id}`}
        >
          WhatsApp
        </a>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest-2 border ${statusDef?.color || "text-white"} border-white/15 hover:border-[#d4af37]`}
              data-testid={`bulk-status-${bulk.id}`}
            >
              {statusDef?.label || bulk.status}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="glass-strong border-white/10">
            {ORDER_STATUSES.map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => onSetStatus(bulk.id, s.value)}
                className="text-white/80 hover:text-white focus:text-white"
                data-testid={`bulk-set-${bulk.id}-${s.value}`}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

/* ---------------- Referrals ---------------- */
function ReferralsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/referrals");
        setItems(res.data || []);
      } catch {
        toast.error("Failed to load referrals");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((r) =>
      r.name?.toLowerCase().includes(s) ||
      r.phone?.includes(s) ||
      r.code?.toLowerCase().includes(s)
    );
  }, [items, q]);

  const totals = useMemo(() => {
    const total = items.reduce((a, r) => a + (r.successful_count || 0), 0);
    const active = items.filter((r) => (r.successful_count || 0) > 0).length;
    return { total, active, all: items.length };
  }, [items]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          <StatChip icon={<Trophy size={14} />} label="Total referrals" value={totals.total} testid="ref-total" />
          <StatChip icon={<Share2 size={14} />} label="Active referrers" value={totals.active} testid="ref-active" />
          <StatChip icon={<Gift size={14} />} label="All customers" value={totals.all} testid="ref-all" />
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone or code…"
            className="pl-9 bg-white/5 border-white/10 text-white"
            data-testid="referrals-search"
          />
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-white/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-white/50">
            No referrers yet. Once customers place orders they'll receive a unique link that appears here.
          </div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest-2 text-white/45 border-b border-white/5">
                  <th className="py-3 px-5">Rank</th>
                  <th className="py-3 px-5">Name</th>
                  <th className="py-3 px-5">Phone</th>
                  <th className="py-3 px-5">Referral code</th>
                  <th className="py-3 px-5 text-right">Successful referrals</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => (
                  <tr key={r.code} className="border-b border-white/5 last:border-0" data-testid={`referrer-row-${r.code}`}>
                    <td className="py-4 px-5 text-[#d4af37] font-display text-lg">
                      #{idx + 1}
                    </td>
                    <td className="py-4 px-5 text-white">{r.name || "—"}</td>
                    <td className="py-4 px-5 text-white/70">{r.phone}</td>
                    <td className="py-4 px-5">
                      <span className="rounded-full border border-white/15 px-3 py-1 text-xs uppercase tracking-widest-2 text-white/80 font-mono">
                        {r.code}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-right">
                      <span className={`font-display text-2xl ${r.successful_count > 0 ? "text-[#d4af37]" : "text-white/30"}`}>
                        {r.successful_count || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const StatChip = ({ icon, label, value, testid }) => (
  <div className="glass rounded-full px-4 py-2 flex items-center gap-3" data-testid={testid}>
    <span className="text-[#d4af37]">{icon}</span>
    <span className="text-xs uppercase tracking-widest-2 text-white/55">{label}</span>
    <span className="font-display text-lg text-white">{value}</span>
  </div>
);

/* ---------------- Products ---------------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/products");
      setProducts(res.data || []);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Product deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Catalog</div>
          <h2 className="font-display text-3xl text-white mt-1">Ready-made products</h2>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="rounded-full bg-[#d4af37] text-black hover:brightness-110"
          data-testid="admin-add-product"
        >
          <Plus size={16} className="mr-1" /> Add product
        </Button>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-white/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        ) : products.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-white/50">
            No products yet. Tap <span className="text-[#d4af37]">Add product</span> to create your first piece.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} className="glass rounded-2xl overflow-hidden" data-testid={`admin-product-${p.id}`}>
                <div className="aspect-[4/5] bg-black relative">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={40} /></div>
                  )}
                  <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest-2 text-white/80 glass px-2 py-1 rounded-full">{p.category}</div>
                </div>
                <div className="p-4">
                  <div className="font-display text-lg text-white">{p.name}</div>
                  <div className="text-xs text-white/50 mt-1 line-clamp-2">{p.description || "—"}</div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-[#d4af37]">{p.price_hint}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditing(p)} className="text-white/60 hover:text-[#d4af37]" data-testid={`edit-product-${p.id}`}><Edit3 size={14} /></button>
                      <button onClick={() => remove(p.id)} className="text-white/60 hover:text-red-400" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductForm
        open={creating || !!editing}
        product={editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        onSaved={load}
      />
    </div>
  );
}

function ProductForm({ open, product, onOpenChange, onSaved }) {
  const MAX_IMAGES = 20;
  const isEdit = !!product;
  const [form, setForm] = useState({
    name: "",
    category: "Rings",
    description: "",
    image_url: "",
    image_urls: [],
    metal: "gold",
    price_hint: "Contact for price",
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (product) {
      const existing = Array.isArray(product.image_urls) && product.image_urls.length
        ? product.image_urls
        : (product.image_url ? [product.image_url] : []);
      setForm({
        name: product.name || "",
        category: product.category || "Rings",
        description: product.description || "",
        image_url: existing[0] || "",
        image_urls: existing,
        metal: product.metal || "gold",
        price_hint: product.price_hint || "Contact for price",
        is_active: product.is_active !== false,
      });
    } else {
      setForm({ name: "", category: "Rings", description: "", image_url: "", image_urls: [], metal: "gold", price_hint: "Contact for price", is_active: true });
    }
  }, [product, open]);

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - form.image_urls.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images per product`);
      e.target.value = "";
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only ${remaining} image${remaining === 1 ? "" : "s"} left — extras skipped`);
    }
    setUploading(true);
    const uploaded = [];
    for (const f of toUpload) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`);
      } catch {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    if (uploaded.length) {
      setForm((s) => {
        const next = [...s.image_urls, ...uploaded];
        return { ...s, image_urls: next, image_url: next[0] || "" };
      });
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setForm((s) => {
      const next = s.image_urls.filter((_, i) => i !== idx);
      return { ...s, image_urls: next, image_url: next[0] || "" };
    });
  };

  const moveImage = (idx, dir) => {
    setForm((s) => {
      const next = [...s.image_urls];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return s;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...s, image_urls: next, image_url: next[0] || "" };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/products/${product.id}`, form);
      else await api.post("/admin/products", form);
      toast.success(isEdit ? "Product updated" : "Product created");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-transparent border-white/10 p-0 overflow-hidden">
        <div className="glass-strong rounded-2xl p-6 md:p-8">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-3xl text-white">{isEdit ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>

          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest-2 text-white/60">
                  Product images
                </Label>
                <span className="text-[10px] uppercase tracking-widest-2 text-white/40">
                  {form.image_urls.length} / {MAX_IMAGES} · first image is the hero
                </span>
              </div>

              {form.image_urls.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {form.image_urls.map((url, idx) => (
                    <div
                      key={`${url}-${idx}`}
                      className="relative aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 group"
                      data-testid={`admin-product-image-tile-${idx}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <div className="absolute top-1.5 left-1.5 text-[9px] tracking-widest-2 uppercase px-1.5 py-0.5 rounded-full bg-[#d4af37] text-black font-medium">
                          Hero
                        </div>
                      )}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-black/50 flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => moveImage(idx, -1)}
                          disabled={idx === 0}
                          className="w-7 h-7 rounded-full glass flex items-center justify-center text-white disabled:opacity-30 hover:bg-[#d4af37] hover:text-black transition"
                          aria-label="Move left"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 1)}
                          disabled={idx === form.image_urls.length - 1}
                          className="w-7 h-7 rounded-full glass flex items-center justify-center text-white disabled:opacity-30 hover:bg-[#d4af37] hover:text-black transition"
                          aria-label="Move right"
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          data-testid={`admin-product-image-remove-${idx}`}
                          className="w-7 h-7 rounded-full glass flex items-center justify-center text-white hover:bg-red-500 transition"
                          aria-label="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex gap-3 items-center">
                <label
                  className={`relative rounded-full glass border border-white/10 hover:border-[#d4af37] px-5 py-2.5 text-sm text-white/80 ${form.image_urls.length >= MAX_IMAGES ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={form.image_urls.length >= MAX_IMAGES}
                    onChange={uploadImage}
                    className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    data-testid="admin-product-image-input"
                  />
                  {uploading ? "Uploading…" : form.image_urls.length ? "Add more images" : "Upload images"}
                </label>
                <span className="text-[11px] text-white/40">
                  Up to {MAX_IMAGES} images · JPG, PNG, WEBP · reorder by hovering a tile
                </span>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest-2 text-white/60">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="mt-3 bg-white/5 border-white/10 text-white" placeholder="e.g. Rose Blossom Pendant" data-testid="admin-product-name" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest-2 text-white/60">Category</Label>
              <select
                value={form.category}
                onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
                className="mt-3 w-full bg-white/5 border border-white/10 rounded-md px-3 h-10 text-white"
                data-testid="admin-product-category"
              >
                {CATALOG_CATEGORIES.filter((c) => c !== "All").map((c) => (
                  <option key={c} value={c} className="bg-black">{c}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest-2 text-white/60">Metal</Label>
              <select
                value={form.metal}
                onChange={(e) => setForm((s) => ({ ...s, metal: e.target.value }))}
                className="mt-3 w-full bg-white/5 border border-white/10 rounded-md px-3 h-10 text-white"
                data-testid="admin-product-metal"
              >
                <option value="gold" className="bg-black">Gold</option>
                <option value="silver" className="bg-black">Silver</option>
                <option value="mixed" className="bg-black">Mixed</option>
              </select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest-2 text-white/60">Price hint</Label>
              <Input value={form.price_hint} onChange={(e) => setForm((s) => ({ ...s, price_hint: e.target.value }))} className="mt-3 bg-white/5 border-white/10 text-white" placeholder="e.g. Contact for price" data-testid="admin-product-price-hint" />
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest-2 text-white/60">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} rows={3} className="mt-3 bg-white/5 border-white/10 text-white" data-testid="admin-product-description" />
            </div>

            <div className="md:col-span-2 flex items-center gap-2">
              <input
                id="is_active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
                data-testid="admin-product-is-active"
              />
              <Label htmlFor="is_active" className="text-white/70 text-sm">Publish on catalog</Label>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white" data-testid="admin-product-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-[#d4af37] text-black hover:brightness-110 min-w-[140px]" data-testid="admin-product-save">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (isEdit ? "Save changes" : "Create product")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
